import { resolveIpHomeCountry } from '@peekapak/country-origin';
import {
  backendRegionForResidency,
  residencyForHomeCountry,
  servicesForBackendRegion,
  type BackendRegion,
  type DataResidency,
} from '@peekapak/common/lib/regionAndResidency';
import { getDataResidencyRuntime } from './runtime';
import {
  DATA_RESIDENCY_STORAGE_KEY,
  readStoredDataResidency,
  resolvePersistedDataResidency,
  resolveStoredDataResidency,
  SupportedDataResidency,
  toSupportedResidency,
} from './residency';

// The SupportedDataResidency enum values are exactly the canonical
// DataResidency strings; bridge the nominal enum type to the canonical union.
function asCanonicalResidency(residency: SupportedDataResidency): DataResidency {
  return residency as unknown as DataResidency;
}

export interface DataResidencyConfig {
  dataResidency: SupportedDataResidency;
  cognitoAwsRegion: string;
  dynamoDbAwsRegion: string;
  lambdaAwsRegion: string;
  apiEndpointSuffix: string;
}

// Per-service AWS regions come from the single source of truth
// (regionAndResidency); only the frontend-specific apiEndpointSuffix is local.
const usaServices = servicesForBackendRegion('us-east-1');
const areServices = servicesForBackendRegion('eu-central-1');

export const DATA_RESIDENCY_CONFIGS: Record<SupportedDataResidency, DataResidencyConfig> = {
  [SupportedDataResidency.USA]: {
    dataResidency: SupportedDataResidency.USA,
    cognitoAwsRegion: usaServices.cognito,
    dynamoDbAwsRegion: usaServices.dynamodb,
    lambdaAwsRegion: usaServices.compute,
    apiEndpointSuffix: '',
  },
  [SupportedDataResidency.ARE]: {
    dataResidency: SupportedDataResidency.ARE,
    cognitoAwsRegion: areServices.cognito,
    dynamoDbAwsRegion: areServices.dynamodb,
    lambdaAwsRegion: areServices.compute,
    apiEndpointSuffix: '-fra',
  },
};

export class DataResidencyManager {
  private static instance: DataResidencyManager;
  private currentDataResidency: SupportedDataResidency = SupportedDataResidency.USA;
  private readonly changeListeners = new Set<() => void>();

  private constructor() {}

  public static getInstance(): DataResidencyManager {
    if (!DataResidencyManager.instance) {
      DataResidencyManager.instance = new DataResidencyManager();
    }
    return DataResidencyManager.instance;
  }

  /**
   * Observe residency changes, for UI that must re-render when the region the
   * session routes to changes - e.g. the home-country control switching the
   * visitor to the eu-central-1 backend, which has no federated sign-in.
   *
   * Deliberately NOT the `userDataResidencyChanged` window event: that one is
   * handled by DataResidencyAwareAuthHelpers, which responds by globally
   * signing the user out of Cognito. Merely picking a home country must not do
   * that, so change notification for observers is kept separate from it.
   *
   * Returns an unsubscribe function.
   */
  public subscribeToChanges(listener: () => void): () => void {
    this.changeListeners.add(listener);
    return () => {
      this.changeListeners.delete(listener);
    };
  }

  private notifyChangeListeners(): void {
    for (const listener of this.changeListeners) {
      try {
        listener();
      } catch (error) {
        // One bad listener must not stop the rest, nor fail the residency change.
        console.warn('A data residency change listener threw:', error);
      }
    }
  }

  /**
   * Set the session's residency WITHOUT persisting. For detected/guessed
   * signals (IP guess at init, the auth pages' resolver output, error
   * fallbacks): guesses are session state, recomputed each visit, and must
   * never gain the authority of a stored value.
   *
   * On a value CHANGE this also refreshes the host app's endpoint roots:
   * consumers read module-level root snapshots, so without the refresh a
   * residency change steers the X-Backend-Region header but keeps sending
   * requests to the previous region's host.
   *
   * Returns when that refresh has completed (it never rejects), so callers
   * that fire a request immediately after - initialization above all - can
   * await routing consistency. Fire-and-forget callers may ignore it.
   */
  public setDataResidency(dataResidency: SupportedDataResidency): Promise<void> {
    const previousDataResidency = this.currentDataResidency;
    this.currentDataResidency = dataResidency;

    if (previousDataResidency !== dataResidency) {
      // On a microtask, not inline: getCurrentDataResidency() can migrate a
      // legacy stored value (and so land here) while a component is reading it
      // during render, and notifying synchronously would set state mid-render.
      void Promise.resolve().then(() => this.notifyChangeListeners());

      try {
        getDataResidencyRuntime().refreshEndpoints();
      } catch (error) {
        console.warn('Could not refresh endpoints after data residency change:', error);
      }
    }

    return Promise.resolve();
  }

  /**
   * Set AND persist the residency. Only for signals with authority: the
   * user's explicit home-country choice, or the account's real region
   * learned at successful sign-in/sign-up/QR/OAuth. The stored value wins
   * over any in-session guess on every read.
   *
   * The written value is the frozen cross-app contract - see
   * DATA_RESIDENCY_STORAGE_KEY.
   */
  public persistDataResidency(dataResidency: SupportedDataResidency): Promise<void> {
    const endpointsRefreshed = this.setDataResidency(dataResidency);
    try {
      localStorage.setItem(DATA_RESIDENCY_STORAGE_KEY, dataResidency);
    } catch (error) {
      console.warn('[data-residency] Could not persist residency:', error);
    }
    return endpointsRefreshed;
  }

  /** Drop the stored residency (e.g. the user cleared their explicit choice). */
  public clearPersistedDataResidency(): void {
    try {
      localStorage.removeItem(DATA_RESIDENCY_STORAGE_KEY);
    } catch (error) {
      console.warn('[data-residency] Could not clear residency:', error);
    }
  }

  /**
   * The authoritative residency, or null when nothing is known. Unlike
   * getCurrentDataResidency this never falls back to the in-memory session
   * guess, so callers can distinguish "known" from "guessed".
   *
   * Applies the full precedence including the explicit home-country choice.
   */
  public getPersistedDataResidency(): SupportedDataResidency | null {
    return resolvePersistedDataResidency();
  }

  public getCurrentDataResidency(): SupportedDataResidency {
    const storedDataResidency = readStoredDataResidency();
    const resolvedDataResidency = resolveStoredDataResidency(storedDataResidency);

    if (resolvedDataResidency) {
      if (resolvedDataResidency !== storedDataResidency) {
        // Rewrite a legacy or non-canonical value in the contract's spelling.
        void this.persistDataResidency(resolvedDataResidency);
      } else {
        this.currentDataResidency = resolvedDataResidency;
      }
    }

    return this.currentDataResidency;
  }

  public getCurrentConfig(): DataResidencyConfig {
    return DATA_RESIDENCY_CONFIGS[this.getCurrentDataResidency()];
  }

  /**
   * The backend region (AWS region string) that serves the current residency.
   * Sent as the `X-Backend-Region` request header so the backend can route
   * without re-deriving the region.
   */
  public getBackendRegion(): BackendRegion {
    return backendRegionForResidency(asCanonicalResidency(this.getCurrentDataResidency()));
  }

  public isAREDataResidency(): boolean {
    return this.getCurrentDataResidency() === SupportedDataResidency.ARE;
  }

  public getApiSubdomain(): string {
    const config = this.getCurrentConfig();
    return config.apiEndpointSuffix === '-fra' ? 'api-fra' : 'api';
  }

  /** The other residency - used by the round-robin sign-in and QR fallback. */
  public getAlternateDataResidency(): SupportedDataResidency {
    return this.getCurrentDataResidency() === SupportedDataResidency.ARE
      ? SupportedDataResidency.USA
      : SupportedDataResidency.ARE;
  }

  public getCognitoConfiguration(dataResidency?: SupportedDataResidency) {
    const targetDataResidency = dataResidency || this.getCurrentDataResidency();
    const { useDevelopmentBackend } = getDataResidencyRuntime();

    if (targetDataResidency === SupportedDataResidency.ARE) {
      return useDevelopmentBackend
        ? import('@peekapak/common/configuration/devCognitoConfigurationFRA.json')
        : import('@peekapak/common/configuration/prodCognitoConfigurationFRA.json');
    }

    return useDevelopmentBackend
      ? import('@peekapak/common/configuration/devCognitoConfiguration.json')
      : import('@peekapak/common/configuration/prodCognitoConfiguration.json');
  }

  public async detectUserDataResidencyFromIP(): Promise<SupportedDataResidency> {
    // Delegate to the single IP-signal source (the unified country-of-origin
    // resolver) and derive residency through the canonical
    // `residencyForHomeCountry` mapping. Unknown/failed lookups fall back to
    // `UNK`, which the canonical mapping derives to the default `USA`.
    const ipHomeCountry = await resolveIpHomeCountry();
    return toSupportedResidency(residencyForHomeCountry(ipHomeCountry ?? 'UNK'));
  }

  public async initializeDataResidency(): Promise<void> {
    // An authoritative signal - the explicit home-country choice, or a
    // residency stored by a prior successful auth. Both are resolved by the one
    // shared chokepoint, so an app can never see a subset of the signals (the
    // MYP-846 failure) or miss the legacy-value migration.
    const persistedDataResidency = resolvePersistedDataResidency();
    if (persistedDataResidency) {
      // Persisted rather than merely set, so an explicit choice that had not
      // yet been written through, and a legacy value, both land in the
      // contract's spelling for any peer app reading after us. Awaited so init
      // resolves only once the endpoint roots match the residency.
      await this.persistDataResidency(persistedDataResidency);
      return;
    }

    // Otherwise detect from the IP guess (geo-places-free; no prompt at init).
    // A guess is session state: NOT persisted, so detection runs again next
    // visit and a later, better signal (geolocation, choice, login) is never
    // blocked by a stored guess.
    const detectedDataResidency = await this.detectUserDataResidencyFromIP();
    await this.setDataResidency(detectedDataResidency);
  }
}

export const dataResidencyManager = DataResidencyManager.getInstance();
